import { NotificationChannel, NotificationMessageCategory } from './notification.models';

const CHANNEL_NUMERIC_MAP: Record<number, NotificationChannel> = {
  0: 'Email',
  1: 'Sms',
  2: 'Push',
  3: 'InApp'
};

const CATEGORY_NUMERIC_MAP: Record<number, NotificationMessageCategory> = {
  0: 'Transactional',
  1: 'Service',
  2: 'Marketing'
};

export function normalizeNotificationChannel(channel: NotificationChannel): string {
  if (typeof channel === 'number') {
    return String(CHANNEL_NUMERIC_MAP[channel] ?? channel);
  }
  return String(channel);
}

export function normalizeNotificationCategory(category: NotificationMessageCategory | null | undefined): string | null {
  if (category === null || category === undefined) {
    return null;
  }
  if (typeof category === 'number') {
    return String(CATEGORY_NUMERIC_MAP[category] ?? category);
  }
  return String(category);
}

export function notificationChannelToApiValue(channel: string | null): string | null {
  if (!channel) {
    return null;
  }
  return channel;
}

export function notificationCategoryToApiValue(category: string | null): string | null {
  if (!category) {
    return null;
  }
  return category;
}

export function canCancelMessage(status: string): boolean {
  const normalized = status.trim().toLowerCase();
  return normalized === 'queued' || normalized === 'scheduled' || normalized === 'processing';
}

export function canResendMessage(status: string): boolean {
  const normalized = status.trim().toLowerCase();
  return normalized === 'failed' || normalized === 'partiallysent' || normalized === 'cancelled';
}

export function maskDestinationHash(hash: string): string {
  const trimmed = hash.trim();
  if (trimmed.length <= 8) {
    return '••••••••';
  }
  return `${trimmed.slice(0, 4)}…${trimmed.slice(-4)}`;
}

export function suppressionReasonLabelKey(reason: string): string {
  const key = reason.trim().toLowerCase();
  switch (key) {
    case 'optout':
      return 'notifications.policy.suppressions.reason.optOut';
    case 'hardbounce':
      return 'notifications.policy.suppressions.reason.hardBounce';
    case 'complaint':
      return 'notifications.policy.suppressions.reason.complaint';
    case 'stopkeyword':
      return 'notifications.policy.suppressions.reason.stopKeyword';
    case 'manual':
      return 'notifications.policy.suppressions.reason.manual';
    default:
      return 'notifications.policy.suppressions.reason.other';
  }
}

export function providerKeyLabel(providerKey: string): string {
  switch (providerKey) {
    case 'smtp':
      return 'SMTP';
    case 'gmail-oauth':
      return 'Gmail';
    case 'kavenegar':
      return 'Kavenegar';
    default:
      return providerKey;
  }
}

export function isEmailSmsChannel(channelKey: string): boolean {
  const normalized = channelKey.trim().toLowerCase();
  return normalized === 'email' || normalized === 'sms';
}

export function buildPlaceholderRows(placeholders: Array<{ key: string; description: string; isRequired: boolean; sampleValue: string | null }>): Array<{
  key: string;
  description: string;
  isRequired: boolean;
  sampleValue: string;
}> {
  return placeholders.map(item => ({
    key: item.key.trim(),
    description: item.description.trim(),
    isRequired: item.isRequired,
    sampleValue: item.sampleValue?.trim() ?? ''
  }));
}

export function placeholderValuesFromRows(rows: Array<{ key: string; sampleValue: string }>): Record<string, string> {
  const values: Record<string, string> = {};
  for (const row of rows) {
    const key = row.key.trim();
    if (!key) {
      continue;
    }
    values[key] = row.sampleValue;
  }
  return values;
}

export function validateSmtpSettings(settings: {
  host: string;
  port: number;
  fromEmail: string;
}): string | null {
  if (!settings.host.trim()) {
    return 'notifications.providers.validation.hostRequired';
  }
  if (!settings.port || settings.port < 1) {
    return 'notifications.providers.validation.portRequired';
  }
  if (!settings.fromEmail.trim()) {
    return 'notifications.providers.validation.fromEmailRequired';
  }
  return null;
}

export function validateTestRecipient(address: string): string | null {
  const trimmed = address.trim();
  if (!trimmed) {
    return 'notifications.providers.validation.recipientRequired';
  }
  if (!trimmed.includes('@') && !/^\+?\d{8,}$/.test(trimmed)) {
    return 'notifications.providers.validation.recipientInvalid';
  }
  return null;
}
